/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { Trans, useTranslation } from 'react-i18next';
import CustomCheckbox from 'components/CustomCheckbox';
import { Link, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import { schemaRequiresTrueValue } from '../rjsf/utils';
import Tooltip from '../../common/Tooltip';
import { uiSchemaProperties } from '../../../shared/enums/uiSchemaProperties';

/**
 * Overwritten CheckboxWidget of RJSF that supports labels with formatted content.
 *
 * Can be used:
 *  - with no formatting by specifying the title field
 *  - with a formatted anchor inside the label by specifying the following config object:
 *    "config": {
 *       "type": "label-with-anchor",
 *       "i18nKey": "i18n.key",
 *       "href": "url"
 *    }
 *
 */

const useStyles = makeStyles(() => ({
  label: ({ hasLink }) => ({
    display: 'unset',
    pointerEvents: hasLink ? 'none' : 'auto',
  }),
  checkbox: {
    pointerEvents: 'auto',
  },
  hidden: {
    visibility: 'hidden',
    opacity: 0,
    height: 0,
    transition: 'visibility 0s, opacity 0.5s linear',
  },
  tooltipLabel: {
    verticalAlign: 'sub',
  },
}));

const getLabel = (schema, label, t) => {
  const href = schema?.config?.href;
  switch (schema?.config?.type) {
    case 'label-with-anchor':
      return (
        <Trans i18nKey={schema?.config?.i18nKey}>
          <Link href={t(href)} underline="always" style={{ pointerEvents: 'auto' }} />
        </Trans>
      );
    case 'label-with-anchor-new-tab':
      return (
        <Trans i18nKey={schema?.config?.i18nKey}>
          <Link
            href={t(href)}
            underline="always"
            variant="body2"
            style={{ pointerEvents: 'auto' }}
            target="_blank"
            rel="noreferrer"
          />
        </Trans>
      );
    default:
      return label;
  }
};

const CheckboxWidget = (props) => {
  const {
    schema,
    uiSchema,
    id,
    value,
    disabled,
    readonly,
    label,
    autofocus,
    onChange,
    onBlur,
    onFocus,
    registry,
  } = props;
  const { shouldApplyChange = () => true } = props;
  const { t } = useTranslation();

  // Because an unchecked checkbox will cause html5 validation to fail, only add
  // the "required" attribute if the field value must be "true", due to the
  // "const" or "enum" keywords
  const required = schemaRequiresTrueValue(schema);

  // eslint-disable-next-line no-empty-pattern
  const _onBlur = ({ target: { value: _value } }) => onBlur(id, _value);
  const _onFocus = ({ target: { value: _value } }) => onFocus(id, _value);
  const _onChange = (_, checked) => {
    if (!shouldApplyChange(checked)) {
      return;
    }
    if (schema?.config?.triggerPostProcess === true) {
      registry.triggerPostProcess(id, checked);
    }
    onChange(checked);
  };

  // Because uiSchema properties don't work with boolean fields we have to use config schema properties
  // https://github.com/rjsf-team/react-jsonschema-form/issues/827
  const tooltip = schema?.config?.tooltip || schema?.tooltip;
  const classes = useStyles({ hasLink: !!schema?.config?.href });

  return (
    <div className={classNames({ [classes.hidden]: uiSchema?.[uiSchemaProperties.UI_HIDDEN] })}>
      <FormControlLabel
        classes={{ root: classes.label }}
        control={(
          <CustomCheckbox
            className={classes.checkbox}
            id={id}
            checked={typeof value === 'undefined' ? false : value}
            required={required}
            disabled={disabled || readonly}
            autoFocus={autofocus}
            onChange={_onChange}
            onBlur={_onBlur}
            onFocus={_onFocus}
          />
        )}
        label={getLabel(schema, label, t)}
      />
      {tooltip && <Tooltip className={classes.tooltipLabel} title={<Trans i18nKey={tooltip} />} />}
    </div>
  );
};

export default CheckboxWidget;
